import { useEffect, useMemo, useState } from 'react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { Leaf, Plus, Pencil, Trash2, Loader2, Check, X } from 'lucide-react';
import type { CropType } from '../../types/cultivar';
import cultivarService from '../../services/cultivar';

function CropTypesAdmin() {
  const [cropTypes, setCropTypes] = useState<CropType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  // Add-top-level state
  const [newParentName, setNewParentName] = useState('');
  const [addingParent, setAddingParent] = useState(false);

  // Add-child state keyed by parent id
  const [childName, setChildName] = useState<Record<number, string>>({});
  const [addingChild, setAddingChild] = useState<number | null>(null);

  // Edit state — id being edited + working name
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [savingId, setSavingId] = useState<number | null>(null);

  const [deletingId, setDeletingId] = useState<number | null>(null);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await cultivarService.getCropTypes();
      if (res.success && res.data) {
        setCropTypes(res.data);
      } else {
        setError('Failed to load crop types');
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load crop types');
    } finally {
      setLoading(false);
    }
  };

  const grouped = useMemo(() => {
    const parents = cropTypes
      .filter((ct) => ct.parent_id === null)
      .sort((a, b) => a.name.localeCompare(b.name));
    return parents.map((parent) => ({
      parent,
      children: cropTypes
        .filter((ct) => ct.parent_id === parent.id)
        .sort((a, b) => a.name.localeCompare(b.name)),
    }));
  }, [cropTypes]);

  const flash = (msg: string) => {
    setInfo(msg);
    setTimeout(() => setInfo(null), 2500);
  };

  const handleAddParent = async () => {
    const name = newParentName.trim();
    if (!name) return;
    setAddingParent(true);
    setError(null);
    try {
      const res = await cultivarService.createCropType(name);
      if (res.success && res.data) {
        setCropTypes((prev) => [...prev, res.data!]);
        setNewParentName('');
        flash(`Added "${name}"`);
      } else {
        setError('Failed to add crop type');
      }
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || e?.message || 'Failed to add crop type');
    } finally {
      setAddingParent(false);
    }
  };

  const handleAddChild = async (parentId: number) => {
    const name = (childName[parentId] || '').trim();
    if (!name) return;
    setAddingChild(parentId);
    setError(null);
    try {
      const res = await cultivarService.createCropType(name, undefined, parentId);
      if (res.success && res.data) {
        setCropTypes((prev) => [...prev, res.data!]);
        setChildName((prev) => ({ ...prev, [parentId]: '' }));
        flash(`Added "${name}"`);
      } else {
        setError('Failed to add subcategory');
      }
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || e?.message || 'Failed to add subcategory');
    } finally {
      setAddingChild(null);
    }
  };

  const startEdit = (ct: CropType) => {
    setEditingId(ct.id);
    setEditName(ct.name);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
  };

  const saveEdit = async (id: number) => {
    const name = editName.trim();
    if (!name) return;
    setSavingId(id);
    setError(null);
    try {
      const res = await cultivarService.updateCropType(id, { name });
      if (res.success && res.data) {
        setCropTypes((prev) => prev.map((c) => (c.id === id ? res.data! : c)));
        cancelEdit();
        flash('Updated');
      } else {
        setError('Failed to update');
      }
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || e?.message || 'Failed to update');
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (ct: CropType) => {
    if (!window.confirm(`Delete "${ct.name}"? This cannot be undone.`)) return;
    setDeletingId(ct.id);
    setError(null);
    try {
      const res = await cultivarService.deleteCropType(ct.id);
      if (res.success) {
        setCropTypes((prev) => prev.filter((c) => c.id !== ct.id && c.parent_id !== ct.id));
        flash(`Deleted "${ct.name}"`);
      } else {
        setError('Failed to delete');
      }
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || e?.message || 'Failed to delete');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Crop Types"
        subtitle="Manage crop categories and their subcategories (e.g. Stone Fruit › Peaches)"
        icon={<Leaf className="h-6 w-6 text-white" />}
      />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-4">
        {/* Status banners */}
        {error && (
          <div className="bg-error-50 border border-error-200 text-error-700 rounded-lg p-3 text-sm">
            {error}
          </div>
        )}
        {info && (
          <div className="bg-success-50 border border-success-200 text-success-700 rounded-lg p-3 text-sm">
            {info}
          </div>
        )}

        {/* Add top-level */}
        <div className="bg-white rounded-xl shadow-sm border border-neutral-200 p-4">
          <h3 className="text-sm font-semibold text-neutral-700 mb-2">Add crop type</h3>
          <div className="flex gap-2">
            <Input
              placeholder="e.g. Stone Fruit"
              value={newParentName}
              onChange={(e) => setNewParentName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAddParent();
              }}
            />
            <Button variant="primary" onClick={handleAddParent} disabled={addingParent || !newParentName.trim()}>
              {addingParent ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Add
            </Button>
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-neutral-200 divide-y divide-neutral-100">
            {grouped.length === 0 && (
              <p className="p-6 text-center text-sm text-neutral-500">
                No crop types yet. Add your first one above.
              </p>
            )}

            {grouped.map(({ parent, children }) => (
              <div key={parent.id} className="p-4">
                {/* Parent row */}
                <div className="flex items-center gap-2">
                  {editingId === parent.id ? (
                    <>
                      <Input
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveEdit(parent.id);
                          if (e.key === 'Escape') cancelEdit();
                        }}
                        autoFocus
                      />
                      <Button variant="primary" size="sm" onClick={() => saveEdit(parent.id)} disabled={savingId === parent.id}>
                        {savingId === parent.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={cancelEdit}>
                        <X className="h-4 w-4" />
                      </Button>
                    </>
                  ) : (
                    <>
                      <span className="flex-1 font-semibold text-neutral-900">{parent.name}</span>
                      <Button variant="ghost" size="sm" onClick={() => startEdit(parent)} aria-label="Edit">
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(parent)}
                        disabled={deletingId === parent.id}
                        aria-label="Delete"
                      >
                        {deletingId === parent.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4 text-error-500" />
                        )}
                      </Button>
                    </>
                  )}
                </div>

                {/* Children */}
                {children.length > 0 && (
                  <ul className="mt-2 ml-6 space-y-1">
                    {children.map((child) => (
                      <li key={child.id} className="flex items-center gap-2 py-1">
                        <span className="text-neutral-400">›</span>
                        {editingId === child.id ? (
                          <>
                            <Input
                              value={editName}
                              onChange={(e) => setEditName(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') saveEdit(child.id);
                                if (e.key === 'Escape') cancelEdit();
                              }}
                              autoFocus
                            />
                            <Button variant="primary" size="sm" onClick={() => saveEdit(child.id)} disabled={savingId === child.id}>
                              {savingId === child.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                            </Button>
                            <Button variant="ghost" size="sm" onClick={cancelEdit}>
                              <X className="h-4 w-4" />
                            </Button>
                          </>
                        ) : (
                          <>
                            <span className="flex-1 text-neutral-700">{child.name}</span>
                            <Button variant="ghost" size="sm" onClick={() => startEdit(child)} aria-label="Edit">
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(child)}
                              disabled={deletingId === child.id}
                              aria-label="Delete"
                            >
                              {deletingId === child.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Trash2 className="h-4 w-4 text-error-500" />
                              )}
                            </Button>
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                )}

                {/* Add child */}
                <div className="mt-2 ml-6 flex gap-2">
                  <Input
                    placeholder={`Add subcategory under ${parent.name}…`}
                    value={childName[parent.id] || ''}
                    onChange={(e) => setChildName((prev) => ({ ...prev, [parent.id]: e.target.value }))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleAddChild(parent.id);
                    }}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleAddChild(parent.id)}
                    disabled={addingChild === parent.id || !(childName[parent.id] || '').trim()}
                  >
                    {addingChild === parent.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default CropTypesAdmin;
